// v1.22.0 · Auditoría interna · llamadas al API, carga por partes de 1 MB, seguimiento de procesos y formatos
import axios from 'axios';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/auditoria`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
export const api = {
  get: (p, params) => axios.get(`${API}${p}`, { ...h(), params }).then((r) => r.data),
  post: (p, body) => axios.post(`${API}${p}`, body, h()).then((r) => r.data),
  put: (p, body) => axios.put(`${API}${p}`, body, h()).then((r) => r.data),
  patch: (p, body) => axios.patch(`${API}${p}`, body, h()).then((r) => r.data),
  del: (p, params) => axios.delete(`${API}${p}`, { ...h(), params }).then((r) => r.data),
  form: (p, fd) => axios.post(`${API}${p}`, fd, h()).then((r) => r.data),
};

export const errMsg = (e, fallback = 'Ocurrió un error') => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x).join(' · ');
  if (d && typeof d === 'object') return d.message || fallback;
  return d || e?.message || fallback;
};

const CHUNK = 1024 * 1024;   // 1 MB por parte (límite del proxy)

/** Sube un archivo (o un .zip completo) por partes y devuelve el proceso que lo analiza. */
export async function uploadFile(companyId, file, { category = '', period = '', accountCode = '', onProgress } = {}) {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const init = new FormData();
  init.append('filename', file.name);
  init.append('total_chunks', total);
  init.append('size', file.size);
  init.append('category', category);
  init.append('period', period);
  init.append('account_code', accountCode);
  const { upload_id: uploadId } = await api.form(`/companies/${companyId}/upload/init`, init);
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', i);
    fd.append('chunk', file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK)), `parte_${i}`);
    let ok = false;
    for (let t = 0; t < 3 && !ok; t += 1) {   // reintenta una parte que falló por la red
      try { await api.form('/upload/chunk', fd); ok = true; } catch (e) { if (t === 2) throw e; await sleep(1200 * (t + 1)); }
    }
    onProgress?.((i + 1) / total);
  }
  const done = new FormData();
  done.append('upload_id', uploadId);
  return api.form('/upload/complete', done);
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Consulta un proceso hasta que termine. */
export async function waitJob(jobId, onTick, everyMs = 1500) {
  for (;;) {
    const j = await api.get(`/jobs/${jobId}`);
    onTick?.(j);
    if (j.status !== 'running') return j;
    await sleep(everyMs);
  }
}

export async function download(path, fallbackName) {
  const r = await axios.get(`${API}${path}`, { ...h(), responseType: 'blob' });
  const cd = r.headers['content-disposition'] || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)/);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ─────────── formatos ───────────
export const money = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—'
  : Number(n).toLocaleString('es-EC', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
export const short = (n) => {
  if (n === null || n === undefined) return '—';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toLocaleString('es-EC', { maximumFractionDigits: 2 })} M`;
  if (a >= 1e3) return `${(n / 1e3).toLocaleString('es-EC', { maximumFractionDigits: 1 })} k`;
  return Number(n).toLocaleString('es-EC', { maximumFractionDigits: 0 });
};
export const pct = (n, dec = 1) => (n === null || n === undefined ? '—' : `${(n * 100).toLocaleString('es-EC', { maximumFractionDigits: dec })} %`);
const MES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MES_L = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const plab = (p) => (p && /^\d{4}-\d{2}/.test(p) ? `${MES[+p.slice(5, 7) - 1]} ${p.slice(2, 4)}` : p || 'Sin período');
export const plong = (p) => (p && /^\d{4}-\d{2}/.test(p) ? `${MES_L[+p.slice(5, 7) - 1]} ${p.slice(0, 4)}` : p || 'Sin período');
export const fdate = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Guayaquil' }) : '—');
export const fsize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// ─────────── colores (paleta validada para daltonismo sobre la superficie #e9edf3) ───────────
export const C = {
  s1: '#2a78d6', s2: '#eb6834', s3: '#1baf7a', s4: '#eda100', ink: '#1f2937', ink2: '#4b5563', muted: '#6b7280',
  grid: '#d5dbe5', brand: '#2F3D8E', good: '#0ca30c',
};
export const SEV = {
  alta: { label: 'Alta', color: '#d03b3b', icon: '▲' },
  media: { label: 'Media', color: '#ec835a', icon: '●' },
  baja: { label: 'Baja', color: '#c98500', icon: '▼' },
};
export const STATUS = {
  abierta: { label: 'Abierta', cls: 'neu-chip-red' },
  aclarada: { label: 'Aclarada', cls: 'neu-chip-amber' },
  resuelta: { label: 'Resuelta', cls: 'neu-chip-green' },
  descartada: { label: 'Descartada', cls: 'neu-chip-gray' },
};
export const KIND_LABEL = {
  plan: 'Plan de cuentas', balance: 'Balance general', pyg: 'Estado de resultados', mayor: 'Libro mayor', banco_resumen: 'Estado de cuenta bancario',
  banco_meses: 'Movimientos bancarios', tarjeta: 'Estado de cuenta de tarjeta', sri_103: 'Formulario 103', sri_104: 'Formulario 104',
  iess_planilla: 'Planillas IESS', cxp_cartera: 'Cartera por pagar', cxc_cartera: 'Cartera vs. contabilidad', presupuesto: 'Presupuesto vs. ejecución',
  anexo_codigos: 'Anexo con códigos', certificados_iess: 'Certificados médicos IESS', aclaracion: 'Aclaración', documento: 'Soporte',
};
